import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Badge,
  Tooltip,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Textarea,
  Input,
  VStack,
  Button,
  Text,
} from "@chakra-ui/react";
import { FiEdit } from "react-icons/fi";
import { ToolConfig, ToolListResponseWrapper } from "build-ts/agent_config";
import { getToolsList, updateTool } from "services/EvaServices";
import Table, { TableColumn, TableFilter, TablePagination } from "../common/Table";


const ToolTable: React.FC = () => {
  const hasMounted = useRef(false);

  // State for data
  const [tools, setTools] = useState<ToolConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<TablePagination>({
    total: 0,
    offset: 0,
    has_more: false,
  });
  const [limit] = useState(50);

  // Tool filters
  const [filters, setFilters] = useState<Record<string, string>>({
    search: "",
  });
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Edit modal state
  const { isOpen: isEditToolOpen, onOpen: onEditToolOpen, onClose: onEditToolClose } = useDisclosure();
  const [editingTool, setEditingTool] = useState<ToolConfig | null>(null);
  const [editFormData, setEditFormData] = useState({
    name_in_agent: "",
    description: "",
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch tools from Darwin API
  const fetchTools = useCallback(async (
      reset = false,
      currentSortBy = "name",
      currentSortOrder = "asc",
      currentFilters: Record<string, string> = filters,
      newOffset?: number
  ) => {
    try {
      if (reset) {
        setLoading(true);
      }
      setError(null);

      const currentOffset = newOffset !== undefined ? newOffset : (reset ? 0 : pagination.offset || 0);
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString(),
        sort_by: currentSortBy,
        sort_order: currentSortOrder,
      });

      if (currentFilters.search && currentFilters.search.trim()) {
        params.append("name", currentFilters.search);
      }

      const result: ToolListResponseWrapper = await getToolsList(params);

      if (result.tools) {
        // Always replace the tools list, never append
        setTools(result.tools);

        if (result.pagination) {
          setPagination({
            total: result.pagination?.total || 0,
            has_more: result.pagination?.has_more || false,
            offset: currentOffset,
          });
        }
      } else {
        // API succeeded but no tools returned
        setTools([]);
        setPagination({
          total: 0,
          has_more: false,
          offset: currentOffset,
        });
      }
    } catch (err) {
      const errorMsg = `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [limit, pagination.offset, filters]);

  // Load data on component mount
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      const loadData = async () => {
        await fetchTools(true);
      };
      loadData();
    }
  }, [fetchTools]);

  // Table event handlers
  const handlePageChange = useCallback(async (offset: number) => {
    await fetchTools(false, sortBy, sortOrder, filters, offset);
  }, [fetchTools, sortBy, sortOrder, filters]);

  const handleSort = useCallback(async (newSortBy: string, newSortOrder: "asc" | "desc") => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    await fetchTools(true, newSortBy, newSortOrder, filters, 0);
  }, [fetchTools, filters]);

  const handleFilterChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
  }, []);

  const handleApplyFilters = useCallback(async () => {
    await fetchTools(true, sortBy, sortOrder, filters, 0);
  }, [fetchTools, sortBy, sortOrder, filters]);

  const handleClearFilters = useCallback(async () => {
    const clearedFilters = { search: "" };
    setFilters(clearedFilters);
    setSortBy("name");
    setSortOrder("asc");
    await fetchTools(true, "name", "asc", clearedFilters, 0);
  }, [fetchTools]);

  const handleEditTool = (tool: ToolConfig) => {
    setEditingTool(tool);
    setEditFormData({
      name_in_agent: tool.name_in_agent || "",
      description: tool.description || "",
    });
    onEditToolOpen();
  };

  const handleUpdateTool = async () => {
    if (!editingTool) return;

    setIsUpdating(true);
    const response = await updateTool(editingTool.id, editFormData);

    // Only update the tool in the list if the update was successful
    if (response) {
      setTools(prevTools =>
        prevTools.map(tool =>
          tool.id === editingTool.id ? { ...tool, name_in_agent: response.name_in_agent, description: response.description } : tool
        )
      );
      onEditToolClose();
      setEditingTool(null);
    }

    setIsUpdating(false);
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Table configuration
  const tableColumns: TableColumn<ToolConfig>[] = [
    {
      key: "name",
      label: "Tool Name",
      sortable: true,
      className: "eva-name-column",
      render: (tool) => (
        <Tooltip label={tool.name} placement="top">
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1} wordBreak="break-word">
            {tool.name}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "name_in_agent",
      label: "Name in Agent",
      className: "eva-name-in-agent-column",
      render: (tool) => (
        <Tooltip label={tool.name_in_agent} placement="top">
          <Text fontSize="sm" color="gray.700" noOfLines={1} wordBreak="break-word">
            {tool.name_in_agent || "-"}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "description",
      label: "Description",
      className: "eva-description-column",
      render: (tool) => (
        <Tooltip label={tool.description} placement="top">
          <Text fontSize="sm" color="gray.600" noOfLines={2} wordBreak="break-word">
            {tool.description}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "version",
      label: "Version",
      className: "eva-version-column",
      render: (tool) => (
        <Badge colorScheme="blue" size="sm">
          v{tool.version}
        </Badge>
      ),
    },
  ];

  const tableFilters: TableFilter[] = [
    {
      key: "search",
      type: "text",
      label: "Search",
      placeholder: "Search tools...",
      width: "300px",
    },
  ];

  const rowActions = (tool: ToolConfig) => (
    <Tooltip label="Edit Tool">
      <IconButton
        icon={<FiEdit />}
        size="sm"
        variant="ghost"
        onClick={() => handleEditTool(tool)}
        aria-label="Edit tool"
      />
    </Tooltip>
  );

  return (
    <>
      <Table
        data={tools}
        columns={tableColumns}
        loading={loading}
        error={error}
        pagination={pagination}
        limit={limit}
        onPageChange={handlePageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        filters={tableFilters}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        rowActions={rowActions}
        getItemId={(tool) => tool.id || ""}
        emptyMessage="No tools found"
        emptySubMessage="Try adjusting your search criteria"
      />

      {/* Tool Edit Modal */}
      <Modal isOpen={isEditToolOpen} onClose={onEditToolClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent className="eva-modal-content">
          <ModalHeader>Edit Tool</ModalHeader>
          <ModalCloseButton />
          <ModalBody className="eva-modal-body eva-small-scrollbar">
            {editingTool && (
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Tool Name</FormLabel>
                  <Input
                    value={editingTool.name}
                    isReadOnly
                    bg="gray.50"
                    cursor="not-allowed"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Name in Agent</FormLabel>
                  <Input
                    value={editFormData.name_in_agent}
                    onChange={(e) => handleEditFormChange("name_in_agent", e.target.value)}
                    placeholder="Enter name used within agent context"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Args in schema json</FormLabel>
                  <Textarea
                    value={editingTool.args_schema_json}
                    isReadOnly
                    rows={4}
                    bg="gray.50"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={editFormData.description}
                    onChange={(e) => handleEditFormChange("description", e.target.value)}
                    placeholder="Enter tool description"
                    rows={8}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onEditToolClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateTool}
              isLoading={isUpdating}
              loadingText="Updating..."
            >
              Update Tool
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ToolTable;
